import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosResponseHeaders,
  RawAxiosResponseHeaders,
} from "axios";
import { axiosSettingObj, httpAPIFromInternet } from "./status";
import { ApiUrl } from "../assets/data/TkApiUrlProperty";

export interface IResponse {
  code: number | string;
  data: any;
  msg: string;
}

export interface httpResponse<T = any, D = any> {
  data: T;
  status: number;
  statusText: string;
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
  config: AxiosRequestConfig<D>;
  request?: any;
}

export const API_REQUEST_START = "apiRequestStart";
export const API_REQUEST_END = "apiRequestEnd";

export type ApiRequestDetail = {
  isRet005?: boolean;
};

let axiosInstance: AxiosInstance = axios.create(axiosSettingObj);
const url404 = `${process.env.REACT_APP_BASE_NAME ?? ""}/error`;
const url401 = `${process.env.REACT_APP_BASE_NAME ?? ""}/error401`;

let isRedirecting = false; // リダイレクト中かどうかを管理するフラグ
const REDIRECT_DELAY = 100; // setTimeoutの延期を管理する構造体

const redirectTo = (url: string) => {
  if (!isRedirecting) {
    isRedirecting = true;
    setTimeout(() => {
      window.location.href = url;
    }, REDIRECT_DELAY);
  }
};

// Maskを開けないAPI
const dontOpenMask = [ApiUrl.API_COM005, ApiUrl.API_COM009, ApiUrl.API_COM004];

const openMask = (url: string) => {
  const lastPart = url.match(/\/[^/]*$/)?.[0] ?? "";
  if (dontOpenMask.includes(lastPart)) return;
  const event = new CustomEvent<ApiRequestDetail>(API_REQUEST_START, {
    detail: { isRet005: url.includes(ApiUrl.API_RET005) },
  });
  window.dispatchEvent(event); // APIマスクを表示するイベントをディスパッチ
};

const closeMask = (url: string) => {
  const lastPart = url.match(/\/[^/]*$/)?.[0] ?? "";
  if (dontOpenMask.includes(lastPart)) return;
  const event = new CustomEvent(API_REQUEST_END);
  window.dispatchEvent(event); // APIマスクを非表示にするイベントをディスパッチ
};

// リクエストインターセプター（リクエストを送信する前のインターセプター）
axiosInstance.interceptors.request.use((config: any) => {
  openMask(config.url);
  // FormData を送る場合はブラウザ/axios に boundary を付与させるため Content-Type を上書きしない
  if (config && config.data instanceof FormData) {
    if (config.headers && "Content-Type" in config.headers) {
      delete config.headers["Content-Type"];
    }
  } else {
    config.headers["Content-Type"] = "application/json";
  }

  // トークン
  if (Object.values(httpAPIFromInternet).includes(config.url)) {
    return Promise.resolve(config);
  }

  const token = JSON.parse(
    sessionStorage.getItem("token") || "{}"
  )?.accessToken;
  token && (config.headers.Authorization = `Bearer ${token}`);

  return Promise.resolve(config);
});

/**
 * エラーが発生した場合にトークンのリフレッシュを処理します。
 *
 * @param error - エラーオブジェクトです。
 * @returns 更新されたレスポンスまたはエラーで拒否される Promise です。
 */
const handleTokenRefresh = async (error: any) => {
  // sessionStorageからトークンを取得する
  const token = sessionStorage.getItem("token");

  // トークンが存在する場合、リフレッシュトークンを取得する
  if (token) {
    const refreshToken = JSON.parse(token).refreshToken;

    // リフレッシュトークンが存在する場合、アクセストークンをリフレッシュするリクエストを送信する
    if (refreshToken) {
      try {
        const res = await axiosInstance.post(ApiUrl.API_COM004, {
          refreshToken,
        });

        if (res.data.status && res.data.status !== 200) {
          // リフレッシュトークンのリフレッシュに失敗した場合、トークンをクリアしてログインページにリダイレクトする
          sessionStorage.clear();
          redirectTo(url401);
        }

        // 新しいアクセストークンを保存する
        sessionStorage.setItem("token", JSON.stringify(res.data));

        // 失敗したリクエストを新しいアクセストークンで再送信する
        error.config.headers["Authorization"] =
          "Bearer " + res.data.accessToken;

        // 新しいレスポンスを返す
        return axiosInstance.request(error.config);
      } catch (error) {
        // リフレッシュトークンのリフレッシュに失敗した場合、トークンをクリアしてログインページにリダイレクトする
        sessionStorage.clear();
        redirectTo(url404);
      }
    } else {
      // リフレッシュトークンが存在しない場合、トップページにリダイレクトする
      redirectTo(url404);
    }
  } else {
    // トークンが存在しない場合、ログインページにリダイレクトする
    redirectTo(url404);
  }

  // デフォルトのエラーレスポンスを返す
  return Promise.reject(error);
};

// レスポンスインターセプター（レスポンスを受信した後のインターセプター）
axiosInstance.interceptors.response.use(
  async (response: AxiosResponse) => {
    closeMask(response.config.url ?? "");
    // ここでバックエンドから返されたデータを解構造化して返し、後続の取得を容易にします
    if (
      response.data.status === 401 &&
      response.config.url !== ApiUrl.API_COM004
    ) {
      return await handleTokenRefresh(response).catch((err: any) => {
        sessionStorage.clear();
        redirectTo(url401);
        return Promise.reject(err);
      });
    }
    return response;
  },
  async (error: any) => {
    const { response, code } = error;
    const status = response?.status;
    closeMask(response?.config.url ?? "");
    const codeError = ["ENOTFOUND", "ECONNREFUSED", "ECONNRESET"];
    if (codeError.includes(code)) {
      redirectTo(url404);
      return Promise.reject(error);
    }
    // タイムアウトの場合はリダイレクトせず、ダイアログを表示する
    if (code === "ECONNABORTED") {
      const event = new CustomEvent("tkApiRequestTimeout");
      window.dispatchEvent(event);
      return Promise.resolve({ data: {} });
    }

    if (status === 401 && response.config.url !== ApiUrl.API_COM004) {
      return await handleTokenRefresh(error).catch((err: any) => {
        sessionStorage.clear();
        redirectTo(url401);
        return Promise.reject(err);
      });
    } else if (status === 403) {
      // 403の場合はリダイレクトを行わず、エラーメッセージをそのまま返す
      return error.response;
    } else {
      redirectTo(url404);
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
